import { useState, useEffect, useCallback, useRef } from 'react';
import { EscortProfile } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { 
  getMainProfileImage, 
  getProfileImages,
  deleteProfileImage as deleteImageService,
  getPlaceholderImage
} from '@/services/profileImage';
import { formatStorageUrl } from '@/services/profileImage/imageUtils';
import { useProfileImageGenerator } from './useProfileImageGenerator';
import { useProfileImageUploader } from './useProfileImageUploader';

// Global set to prevent repeated loading attempts for the same profile ID
// (Could potentially be moved or managed differently if causing issues)
const globalInProgressLoads = new Set<string>();

// Interface for the main hook's return value
interface UseProfileImagesReturn {
  profileImages: Map<string, string>;
  isLoading: boolean;
  loadingErrors: Map<string, string>; // Specific errors during initial load
  generationErrors: Map<string, string>; // Errors from generator hook
  uploadErrors: Map<string, string>; // Errors from uploader hook
  isGenerating: Map<string, boolean>; // State from generator hook
  isUploading: Map<string, boolean>; // State from uploader hook
  loadImagesForProfiles: (profilesToLoad: EscortProfile[]) => Promise<void>;
  generateImageForProfile: (profile: EscortProfile, showToast?: boolean) => Promise<void>; // Modified to update state
  generateBatchImages: (profileBatch: EscortProfile[]) => Promise<void>; // Modified to update state
  uploadImageForProfile: (profileId: string, imageFile: File) => Promise<void>; // Modified to update state
  deleteProfileImage: (profileId: string, imageUrl: string) => Promise<void>;
  getDisplayImage: (profileId: string) => string; // Helper to get current image or placeholder
}

export const useProfileImages = (initialProfiles: EscortProfile[] = []): UseProfileImagesReturn => {
  const [profileImages, setProfileImages] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingErrors, setLoadingErrors] = useState<Map<string, string>>(new Map());
  const [processedProfiles, setProcessedProfiles] = useState<Set<string>>(new Set());
  const profilesRef = useRef<EscortProfile[]>(initialProfiles);
  const { toast } = useToast();

  // Use the extracted hooks
  const {
    isGenerating,
    errors: generationErrors,
    generateImageForProfile: generateImageService,
    generateBatchImages: generateBatchService,
    generatedImagesCache
  } = useProfileImageGenerator();

  const {
    isUploading,
    uploadErrors,
    uploadImageForProfile: uploadImageService
  } = useProfileImageUploader();

  useEffect(() => {
    profilesRef.current = initialProfiles;
    // Reset processed profiles if initial profiles change significantly?
    // Or simply let loadImagesForProfiles handle new ones.
  }, [initialProfiles]);

  // Function to load images (primarily main image for now) for a set of profiles
  const loadImagesForProfiles = useCallback(async (profilesToLoad: EscortProfile[]) => {
    if (!profilesToLoad || profilesToLoad.length === 0) {
      setIsLoading(false);
      return;
    }

    const profilesToActuallyLoad = profilesToLoad.filter(p => 
        !processedProfiles.has(p.id) && !globalInProgressLoads.has(p.id));

    if (profilesToActuallyLoad.length === 0) {
      // console.log("No new profiles need image loading.");
      // Ensure loading state is false if all initial profiles were already processed
      if(isLoading && profilesToLoad.every(p => processedProfiles.has(p.id))) {
        setIsLoading(false);
      }
      return;
    }

    // console.log(`Loading images for ${profilesToActuallyLoad.length} new profiles...`);
    setIsLoading(true);
    const currentLoadingErrors = new Map(loadingErrors);
    const currentlyProcessing = new Set<string>(); // Track profiles processed in *this* batch

    const imageLoadPromises = profilesToActuallyLoad.map(async (profile) => {
      if (globalInProgressLoads.has(profile.id)) return; // Double check

      globalInProgressLoads.add(profile.id);
      currentlyProcessing.add(profile.id);
      
      try {
        // Get the image identifier (path or UUID)
        const mainImageIdentifier = await getMainProfileImage(profile.id);
        if (mainImageIdentifier) {
         // Format the identifier into a full URL before storing
          const fullUrl = formatStorageUrl(mainImageIdentifier);
          setProfileImages(prev => new Map(prev).set(profile.id, fullUrl));
        } else {
         // console.log(`No main image found for ${profile.displayName}. Placeholder might be used.`);
          // Optionally set a specific state or rely on getDisplayImage to handle missing images
        }
        currentLoadingErrors.delete(profile.id); // Clear previous errors on successful load check
      } catch (error) {
        console.error(`Error loading image for profile ${profile.id}:`, error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        currentLoadingErrors.set(profile.id, errorMessage);
      } finally {
        globalInProgressLoads.delete(profile.id);
      }
    });

    await Promise.all(imageLoadPromises);

    setProcessedProfiles(prev => new Set([...prev, ...currentlyProcessing]));
    setLoadingErrors(currentLoadingErrors);
    setIsLoading(false);
   // console.log(`Finished loading batch. Processed: ${processedProfiles.size}`);

  }, [processedProfiles, isLoading, loadingErrors]);

  // Initial load effect
  useEffect(() => {
    // Use initialProfiles directly instead of profilesRef.current
    // console.log("useProfileImages initial load/update effect. Profiles count:", initialProfiles.length);
    if (initialProfiles.length > 0) {
      loadImagesForProfiles(initialProfiles);
    } else {
      setIsLoading(false); // No profiles, not loading
    }
    // Add initialProfiles to dependency array to reload when it changes
  }, [initialProfiles, loadImagesForProfiles]);

  // ---- Wrappers for Generator/Uploader hooks to update local state ----

  const generateImageForProfile = useCallback(async (profile: EscortProfile, showToast: boolean = true) => {
    try {
      const imageUrl = await generateImageService(profile, showToast);
      if (imageUrl) {
        setProfileImages(prev => new Map(prev).set(profile.id, imageUrl));
        // Ensure placeholder is removed if it existed
        if(profileImages.get(profile.id)?.includes('placeholder')) {
           setProfileImages(prev => new Map(prev).set(profile.id, imageUrl));
        }
      }
    } catch (error) {
      // Error is already handled (toast, state) within useProfileImageGenerator
      console.error("Generation failed in parent hook:", error);
    }
  }, [generateImageService, profileImages]);

  const generateBatchImages = useCallback(async (profileBatch: EscortProfile[]) => {
    await generateBatchService(profileBatch);
    // After batch generation, update the main state with any newly cached images
    // This assumes generateBatchService updates the generatedImagesCache ref
    setProfileImages(prev => {
      const updated = new Map(prev);
      let changed = false;
      for (const profile of profileBatch) {
        const cachedUrl = generatedImagesCache.current.get(profile.id);
        if (cachedUrl && prev.get(profile.id) !== cachedUrl) {
          updated.set(profile.id, cachedUrl);
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [generateBatchService, generatedImagesCache]);

  const uploadImageForProfile = useCallback(async (profileId: string, imageFile: File) => {
    try {
      const imageUrl = await uploadImageService(profileId, imageFile);
      // Update state with the new image URL
      setProfileImages(prev => new Map(prev).set(profileId, imageUrl));
    } catch (error) {
       // Error is already handled (toast, state) within useProfileImageUploader
      console.error("Upload failed in parent hook:", error);
    }
  }, [uploadImageService]);

  // ---- Deletion Logic ----

  const deleteProfileImage = useCallback(async (profileId: string, imageUrl: string) => {
    // Optimistic UI update: remove image immediately
    setProfileImages(prev => {
      const updated = new Map(prev);
      if (updated.get(profileId) === imageUrl) { // Only remove if it's the current image
        updated.delete(profileId);
      }
      return updated;
    });

    try {
      console.log(`🗑️ Deleting image ${imageUrl} for profile ${profileId}`);
      await deleteImageService(profileId, imageUrl);
      toast({ title: 'Image supprimée', description: 'L\'image a été supprimée avec succès.' });
      // Potentially need to fetch the new main image or set a placeholder?
      // For now, just removing it relies on getDisplayImage

    } catch (error) {
      console.error(`❌ Erreur lors de la suppression de l\'image ${imageUrl} pour ${profileId}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      // Revert optimistic update
      setProfileImages(prev => new Map(prev).set(profileId, imageUrl));
      toast({
        title: 'Échec de la suppression',
        description: `Impossible de supprimer l\'image : ${errorMessage}`,
        variant: 'destructive',
      });
    }
  }, [toast]);

  // ---- Helper Function ----

  const getDisplayImage = useCallback((profileId: string): string => {
    const imageUrl = profileImages.get(profileId);
    if (imageUrl) {
      return imageUrl;
    }
    // Find the profile object to pass to the placeholder function
    const profile = profilesRef.current.find(p => p.id === profileId);
    return getPlaceholderImage(profile);
  }, [profileImages, profilesRef]);

  return {
    profileImages,
    isLoading,
    loadingErrors,
    generationErrors,
    uploadErrors,
    isGenerating,
    isUploading,
    loadImagesForProfiles,
    generateImageForProfile,
    generateBatchImages,
    uploadImageForProfile,
    deleteProfileImage,
    getDisplayImage,
  };
};
